import IconAlert from 'components/common/IconAlert';
import PageHeader from 'components/common/PageHeader';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  FormLabel,
  ListGroup,
  ListGroupItem,
  Modal,
  Nav,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip
} from 'react-bootstrap';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import corner3 from 'assets/img/illustrations/corner-3.png';
import Flex from 'components/common/Flex';
import _ from 'lodash';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { deleteSavedExport } from 'stores/app';
import { showAlert } from 'stores/modal';
import api from 'util/api';
import logger from 'util/logger';
import DatePicker from 'react-datepicker';
// Client-side PDF generators removed in favor of server export APIs.
import {
  faCalendarAlt,
  faCheckCircle,
  faCloudDownloadAlt,
  faExclamationCircle,
  faInfoCircle,
  faRetweet,
  faTrashAlt
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import config from 'util/config';
import userHelper from 'helpers/userHelper';
import {
  useGetDataExportMonthsQuery,
  useGetSavedExportsQuery
} from 'rtk/reports.slice';
import FalconComponentCard from 'components/common/FalconComponentCard';

const savedExportMatchesMonthRange = (exportItem, range, reportType) => {
  if (!exportItem?.Start || !exportItem?.End) {
    return false;
  }
  if (exportItem.ReportType !== reportType) {
    return false;
  }
  const toUtcDateKey = value => moment.utc(value).format('YYYY-MM-DD');
  const rangeStartKey = toUtcDateKey(range.begin);
  const rangeEndKey = toUtcDateKey(range.end || range.begin);
  const exportStartKey = toUtcDateKey(exportItem.Start);
  const exportEndKey = toUtcDateKey(exportItem.End);
  return exportStartKey === rangeStartKey && exportEndKey === rangeEndKey;
};

const findSavedExportForMonth = (savedExports, name, range, reportType) => {
  return _.find(
    savedExports,
    e => e.Name === name || savedExportMatchesMonthRange(e, range, reportType)
  );
};

const JuratReportingList = () => {
  const reportPrefix = 'month-report';
  const posReportPrefix = 'pos-month-report';
  // Reports are generated via server APIs (no client-side paging).

  const [generatingReportsDisplayKeys, setGeneratingReportsDisplayKeys] =
    useState([]);

  const [showReportNameModal, setShowReportNameModal] = useState(false);
  const [reportNameInput, setReportNameInput] = useState('');
  const [pendingReportRequest, setPendingReportRequest] = useState(null);
  // const [savedExportData, setSavedExportData] = useState(null);
  // const [savedExportData, setSavedExportData] = useState(null);
  // const [dataExportMonths, setDataExportMonths] = useState(null);
  const [deletingSavedExport, setDeletingSavedExport] = useState(false);
  const [downloadingExport, setDownloadingExport] = useState(false);
  const [enbaleCustomReportExport, setEnbaleCustomReportExport] =
    useState(false);
  const [localExportReport, setLocalExportReport] = useState([]);
  const [reportRange, setReportRange] = useState({
    start: '',
    end: ''
  });
  const [downloadReportsContent, setDownloadReportsContent] = useState([]);
  const dispatch = useDispatch();

  const isGeneratingAnyReport = generatingReportsDisplayKeys.length > 0;

  const { userProfile, userMaster } = useSelector(state => state.app);
  const [selectedCommissionProfile, setSelectedCommissionProfile] =
    useState('');

  const { subscriptionStatus } = useSelector(state => state.subscription);
  let activeSubscriptions = _.get(subscriptionStatus, 'Active', []);

  const {
    data: savedExportRes,
    isFetching: isFetchingSavedExports,
    isLoading: isLoadingSavedExports,
    isError: savedExportsError,
    refetch: refetchSavedExports
  } = useGetSavedExportsQuery();

  const {
    data: monthsRes,
    isFetching: isFetchingMonths,
    isLoading: isLoadingMonths,
    isError: monthsError,
    refetch: refetchMonths
  } = useGetDataExportMonthsQuery();

  const loading =
    isFetchingSavedExports ||
    isLoadingSavedExports ||
    isFetchingMonths ||
    isLoadingMonths;

  const refetch = () => {
    refetchSavedExports();
    refetchMonths();
  };

  const savedExportData = useMemo(() => {
    return [...(savedExportRes || [])].sort((a, b) => {
      return new Date(b.Created) - new Date(a.Created);
    });
  }, [savedExportRes]);

  const dataExportMonths = monthsRes || [];

  const laodingReports = isFetchingSavedExports || isLoadingSavedExports;

  const loadingDisplayMonthRange =
    isFetchingSavedExports ||
    isLoadingSavedExports ||
    isFetchingMonths ||
    isLoadingMonths;

  useEffect(() => {
    const savedExports = savedExportData || [];
    // setSavedExportData(sortedRes);
    setGeneratingReportsDisplayKeys(prev =>
      prev.filter(key => !savedExports.find(e => e.Name === key))
    );
    // setDataExportMonths(months);
  }, [savedExportData]);

  const isProfileActive = profile => {
    return (
      profile?.Active &&
      moment()
        .startOf('day')
        .isSameOrBefore(moment(profile?.ExpirationDate).utc())
    );
  };

  const getDisplayProfiles = () => {
    const profiles = userProfile || [];
    const defaults = profiles.filter(p => p?.IsDefault);
    const actives = profiles.filter(p => !p?.IsDefault && isProfileActive(p));
    return [...defaults, ...actives] || [];
  };

  const changeCommissionProfile = value => {
    setSelectedCommissionProfile(value);
  };

  const getSelectedProfileTitle = () => {
    if (!selectedCommissionProfile) {
      return 'Exporting all commission profiles';
    }
    const profiles = getDisplayProfiles();
    const selected = profiles.find(p => p.GUID === selectedCommissionProfile);
    if (selected) {
      return `${selected.CommissionedName} ${selected.CommissionNumber} / ${
        userHelper.getTimezoneObj(selected.LocalTimezone)?.name
      }`;
    }
    return 'Select commission profile';
  };

  // Client-side report generation removed; all reports use server export APIs.

  const removeSavedExportReport = guid => {
    confirmAlert({
      //title: 'Confirmation',
      message: 'Are you sure you want to delete this report?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => proceedToDeleteExportReport(guid)
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const proceedToDeleteExportReport = guid => {
    setDeletingSavedExport(guid);
    dispatch(deleteSavedExport(guid))
      .then(() => {
        setDeletingSavedExport(false);
        getReportsDisplayDateRange();
      })
      .catch(() => {
        setDeletingSavedExport(false);
      });
  };

  const downloadExportedSavedPdf = guid => {
    setDownloadingExport(guid);
    api.JournalExport.download(guid)
      .then(pdfBlob => {
        setDownloadingExport(false);
        const pdfUrl = URL.createObjectURL(pdfBlob);
        setDownloadReportsContent(downloadReportsContent => [
          ...downloadReportsContent,
          { guid: guid, content: pdfUrl }
        ]);
        window.open(pdfUrl);
      })
      .catch(() => {
        setDownloadingExport(false);
      });
  };

  // Proof-of-sequentiality exports are also generated via server APIs.

  const generatePOSReportForDateRange = (
    displayKey,
    mStartDate,
    mEndDate,
    selectedCommissionProfile,
    reportName
  ) => {
    const effectiveDisplayKey = (reportName || '').trim() || displayKey;
    //need to startOf/endOf your dates
    //also utc
    if (isGeneratingAnyReport) {
      toast.warning('System is currently generating a report. Please wait', {
        theme: 'colored',
        position: 'bottom-center',
        icon: faInfoCircle,
        toastId: 'report'
      });
      return;
    }

    let createdAfterDate = moment(mStartDate).startOf('d').utc();
    let createdBeforeDate = moment(mEndDate).startOf('d').utc();
    createdBeforeDate = createdBeforeDate
      .add(23, 'hours')
      .add(59, 'minutes')
      .add(59, 'seconds');

    setGeneratingReportsDisplayKeys(prev =>
      _.uniq(
        _.concat(
          effectiveDisplayKey !== displayKey
            ? [displayKey, effectiveDisplayKey]
            : [effectiveDisplayKey],
          prev
        )
      )
    );

    const data = {
      Start: createdAfterDate.local().format('YYYY-MM-DD'),
      End: createdBeforeDate.local().format('YYYY-MM-DD'),
      DisplayKey: effectiveDisplayKey,
      Format: 'pdf',
      ReportType: 'sequentiality',
      ...(selectedCommissionProfile
        ? { CommissionProfile: selectedCommissionProfile }
        : {})
    };

    api.JournalExport.generate(data)
      .then(res => {
        if (!res?.Success || res?.EntryCount === 0) {
          toast.error(res?.Message || 'Failed to start report generation.', {
            theme: 'colored',
            position: 'bottom-center',
            toastId: 'report-error'
          });
          setGeneratingReportsDisplayKeys(prev =>
            prev.filter(
              key => key !== effectiveDisplayKey && key !== displayKey
            )
          );
          return null;
        }

        const exportGuid = res?.ExportGuid;
        if (!exportGuid) {
          toast.error('Something went wrong. Please try again.', {
            theme: 'colored',
            position: 'bottom-center',
            icon: faExclamationCircle,
            toastId: 'report-error'
          });

          setGeneratingReportsDisplayKeys(prev =>
            prev.filter(
              key => key !== effectiveDisplayKey && key !== displayKey
            )
          );
          return null;
        }

        if (res.Message) {
          toast.success(res.Message, {
            theme: 'colored',
            position: 'bottom-center',
            icon: faCheckCircle,
            toastId: 'report-success'
          });
        }

        return api.JournalExport.download(exportGuid)
          .then(pdfBlob => {
            if (!pdfBlob || !(pdfBlob instanceof Blob)) {
              dispatch(
                showAlert(
                  'Problem generating report',
                  'The server did not return a PDF. Please try again.'
                )
              );
              return null;
            }
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl);
            getReportsDisplayDateRange();
            return null;
          })
          .catch(err => {
            logger.log('download not ready/failed', err);
            toast.success(
              'Processing journal export. We will notify you via email once it is ready for download.',
              {
                theme: 'colored',
                position: 'bottom-center',
                icon: faCheckCircle,
                toastId: 'report-success'
              }
            );
            getReportsDisplayDateRange();
            return null;
          })
          .finally(() => {
            setGeneratingReportsDisplayKeys(prev =>
              prev.filter(
                key => key !== effectiveDisplayKey && key !== displayKey
              )
            );
          });
      })
      .catch(err => {
        logger.log('error requesting server POS export', err);
        setGeneratingReportsDisplayKeys(prev =>
          prev.filter(key => key !== effectiveDisplayKey && key !== displayKey)
        );
        toast.error('Failed to start report generation. Please try again.', {
          theme: 'colored',
          position: 'bottom-center',
          toastId: 'report-error'
        });
      });
  };

  const generateReportForCustomDateRange = reportName => {
    let mStartDate = moment(reportRange.start).utc().local();

    let mEndDate = moment(reportRange.end).utc().local();
    let displayKey =
      mStartDate.format('YYYY-MM-DD') + '-to-' + mEndDate.format('YYYY-MM-DD');
    const effectiveDisplayKey = (reportName || '').trim() || displayKey;
    if (generatingReportsDisplayKeys.includes(effectiveDisplayKey)) {
      toast.warning('This report is already generating. Please wait.', {
        theme: 'colored',
        position: 'bottom-center',
        icon: faInfoCircle,
        toastId: 'report'
      });
      return;
    }
    if (
      config.generateReportOnServer &&
      generatingReportsDisplayKeys.length > 0
    ) {
      toast.warning(
        "System is currently generating a report. Please wait, then click refresh to check when it's ready.",
        {
          theme: 'colored',
          position: 'bottom-center',
          icon: faInfoCircle,
          toastId: 'report'
        }
      );
      return;
    }
    setLocalExportReport([
      ...localExportReport,
      {
        //displayKey: ++displayKeyCounter,
        displayKey: effectiveDisplayKey,
        begin: mStartDate.format('YYYY-MM-DD'),
        title: 'Report',
        end: mEndDate.format('YYYY-MM-DD'),
        signingCount: 0
      }
    ]);
    generateReportForDateRange(
      displayKey,
      mStartDate,
      mEndDate,
      selectedCommissionProfile,
      reportName
    );
  };

  const generateReportForDateRange = (
    displayKey,
    mStartDate,
    mEndDate,
    selectedCommissionProfile,
    reportName
  ) => {
    const effectiveDisplayKey = (reportName || '').trim() || displayKey;
    if (isGeneratingAnyReport) {
      toast.warning('System is currently generating a report. Please wait', {
        theme: 'colored',
        position: 'bottom-center',
        icon: faInfoCircle,
        toastId: 'report'
      });
      return;
    }
    //need to startOf/endOf your dates
    //also utc
    logger.log('displayKey', displayKey);
    logger.log('mStartDate', mStartDate);
    logger.log('mEndDate', mEndDate);
    logger.log('selectedCommissionProfile', selectedCommissionProfile);
    let createdAfterDate = moment(mStartDate).startOf('d').utc();
    let createdBeforeDate = moment(mEndDate).startOf('d').utc();
    createdBeforeDate = createdBeforeDate
      .add(23, 'hours')
      .add(59, 'minutes')
      .add(59, 'seconds');
    setGeneratingReportsDisplayKeys(prev =>
      _.uniq(
        _.concat(
          effectiveDisplayKey !== displayKey
            ? [displayKey, effectiveDisplayKey]
            : [effectiveDisplayKey],
          prev
        )
      )
    );
    const data = {
      Start: createdAfterDate.local().format('YYYY-MM-DD'),
      End: createdBeforeDate.local().format('YYYY-MM-DD'),
      DisplayKey: effectiveDisplayKey,
      Format: 'pdf',
      ReportType: 'daterange',
      ...(selectedCommissionProfile
        ? { CommissionProfile: selectedCommissionProfile }
        : {})
    };

    api.JournalExport.generate(data)
      .then(res => {
        if (!res?.Success || res?.EntryCount === 0) {
          toast.error(res?.Message || 'Failed to start report generation.', {
            theme: 'colored',
            position: 'bottom-center',
            toastId: 'report-error'
          });
          setGeneratingReportsDisplayKeys(prev =>
            prev.filter(
              key => key !== effectiveDisplayKey && key !== displayKey
            )
          );
          return null;
        }

        const exportGuid = res?.ExportGuid;
        if (!exportGuid) {
          toast.error('Something went wrong. Please try again.', {
            theme: 'colored',
            position: 'bottom-center',
            icon: faCheckCircle,
            toastId: 'report-error'
          });
          setGeneratingReportsDisplayKeys(prev =>
            prev.filter(
              key => key !== effectiveDisplayKey && key !== displayKey
            )
          );
          return null;
        }

        if (res.Message) {
          toast.success(res.Message, {
            theme: 'colored',
            position: 'bottom-center',
            icon: faCheckCircle,
            toastId: 'report-success'
          });
        }

        return api.JournalExport.download(exportGuid)
          .then(pdfBlob => {
            if (!pdfBlob || !(pdfBlob instanceof Blob)) {
              dispatch(
                showAlert(
                  'Problem generating report',
                  'The server did not return a PDF. Please try again.'
                )
              );
              return null;
            }
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl);
            getReportsDisplayDateRange();
            return null;
          })
          .catch(err => {
            logger.log('download not ready/failed', err);
            toast.success(
              'Processing journal export. We will notify you via email once it is ready for download.',
              {
                theme: 'colored',
                position: 'bottom-center',
                icon: faCheckCircle,
                toastId: 'report-success'
              }
            );
            getReportsDisplayDateRange();
            return null;
          })
          .finally(() => {
            logger.log('Completed journal export request cleanup', {
              reportDisplayKey: effectiveDisplayKey
            });
            setGeneratingReportsDisplayKeys(prev =>
              prev.filter(
                key => key !== effectiveDisplayKey && key !== displayKey
              )
            );
          });
      })
      .catch(err => {
        logger.log('error requesting server report export', err);
        setGeneratingReportsDisplayKeys(prev =>
          prev.filter(key => key !== effectiveDisplayKey && key !== displayKey)
        );
        toast.error('Failed to start report generation. Please try again.', {
          theme: 'colored',
          position: 'bottom-center',
          toastId: 'report-error'
        });
      });
  };

  const refreshReportList = () => {
    getReportsDisplayDateRange();
  };

  const getReportsDisplayDateRange = () => {
    refetchSavedExports();
    refetchMonths();
    // Listing is now driven by RTK Query caching; keep function for existing call sites.
  };

  const renderPageOfReportLinks = () => {
    //logger.log('render page of links', this.state);
    // let now = moment().utc();
    // let userCreatedMonth = moment.utc(userMaster.Created).startOf('month');

    // let displayDateRanges = [];
    // let curMonthEnd = moment(now);
    // let curMonthStart = moment(now).startOf('month');
    // //curMonthEnd = curMonthEnd.add(23, 'hours').add(59, 'minutes').add(59, 'seconds');
    // while (curMonthStart.isSameOrAfter(userCreatedMonth, 'month')) {
    //   displayDateRanges.push({
    //     displayKey: displayDateRanges.length + 1,
    //     begin: curMonthStart,
    //     end: curMonthEnd
    //   });

    //   curMonthEnd = moment(curMonthEnd).subtract(1, 'month').endOf('month');
    //   curMonthStart = moment(curMonthStart)
    //     .subtract(1, 'month')
    //     .startOf('month');
    // }

    return (
      <Col
        sm={{ size: 10 }}
        className={loadingDisplayMonthRange ? 'refreshing-skeleton' : undefined}
      >
        <ListGroup>
          {_.map(dataExportMonths, function (range) {
            let startDate = moment(range.begin).local().startOf('month');
            let endDate = moment(
              moment(range.begin).local().endOf('month').format('YYYY-MM-DD')
            );

            let monthlyReportKey = reportPrefix + range.displayKey;
            let monthlyPOSReportKey = posReportPrefix + range.displayKey;

            let isGeneratingThisMonthlyReport = !!_.find(
              generatingReportsDisplayKeys,
              displayKey => monthlyReportKey === displayKey
            );
            let isGeneratingThisPOSMonthlyReport = !!_.find(
              generatingReportsDisplayKeys,
              displayKey => monthlyPOSReportKey === displayKey
            );

            let isExportSavedOnServer = findSavedExportForMonth(
              savedExportData,
              monthlyReportKey,
              range,
              'daterange'
            );

            let isPOSExportSavedOnServer = findSavedExportForMonth(
              savedExportData,
              monthlyPOSReportKey,
              range,
              'sequentiality'
            );
            return (
              <ListGroupItem
                key={'range-' + range.displayKey}
                style={{
                  lineHeight: '40px',
                  borderInline: 'none',
                  borderTop: 'none'
                }}
                className="d-md-flex px-0"
              >
                <div style={{ flex: 1 }}>{range.title}</div>
                <div className="d-inline-block text-right" style={{ flex: 2 }}>
                  {!isExportSavedOnServer && (
                    <Button
                      variant={
                        activeSubscriptions.length == 0 ? 'warning' : 'primary'
                      }
                      disabled={
                        isGeneratingThisMonthlyReport ||
                        activeSubscriptions.length == 0
                      }
                      onClick={() =>
                        openReportNameModalForRequest({
                          type: 'daterange',
                          displayKey: monthlyReportKey,
                          startDate,
                          endDate,
                          selectedCommissionProfile
                        })
                      }
                    >
                      {activeSubscriptions.length == 0 && (
                        <FontAwesomeIcon icon={faExclamationCircle} />
                      )}
                      {isGeneratingThisMonthlyReport && <Spinner size="sm" />}{' '}
                      {isGeneratingThisMonthlyReport
                        ? 'Generating ...'
                        : 'Generate'}
                    </Button>
                  )}

                  {isExportSavedOnServer && (
                    <>
                      <OverlayTrigger
                        placement="auto"
                        overlay={
                          <Tooltip>
                            Created on{' '}
                            {moment(isExportSavedOnServer.Created)
                              .local()
                              .format('MMMM Do YYYY, h:mm:ss a')}{' '}
                            <br />
                            {isExportSavedOnServer.AdditionalData}
                          </Tooltip>
                        }
                      >
                        <a
                          className={`btn ${
                            _.includes(
                              _.map(downloadReportsContent, 'guid'),
                              isExportSavedOnServer.GUID
                            )
                              ? 'btn-success'
                              : 'btn-primary'
                          }`}
                          disabled={
                            isExportSavedOnServer &&
                            isExportSavedOnServer.GUID === downloadingExport
                          }
                          download={`journal-export-${isExportSavedOnServer.GUID}.pdf`}
                          href={
                            _.includes(
                              _.map(downloadReportsContent, 'guid'),
                              isExportSavedOnServer.GUID
                            )
                              ? _.find(
                                  downloadReportsContent,
                                  d => d.guid == isExportSavedOnServer.GUID
                                )?.content
                              : '#'
                          }
                          onClick={e => {
                            if (
                              !_.includes(
                                _.map(downloadReportsContent, 'guid'),
                                isExportSavedOnServer.GUID
                              )
                            ) {
                              e.preventDefault();
                              downloadExportedSavedPdf(
                                isExportSavedOnServer.GUID
                              );
                            }
                          }}
                        >
                          {isExportSavedOnServer.GUID === downloadingExport ? (
                            <>
                              <Spinner size="sm" /> Downloading...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faCloudDownloadAlt} />{' '}
                              Download
                            </>
                          )}
                        </a>
                      </OverlayTrigger>{' '}
                      &nbsp;
                      <OverlayTrigger
                        placement="auto"
                        overlay={
                          <Tooltip>
                            Auto delete by{' '}
                            {moment(isExportSavedOnServer.Expires)
                              .local()
                              .format('MMMM Do YYYY, h:mm:ss a')}
                          </Tooltip>
                        }
                      >
                        <span>
                          {deletingSavedExport ===
                          isExportSavedOnServer.GUID ? (
                            <Spinner size="sm" />
                          ) : (
                            <Button
                              variant="outline-danger"
                              onClick={() =>
                                removeSavedExportReport(
                                  isExportSavedOnServer.GUID
                                )
                              }
                            >
                              <FontAwesomeIcon icon={faTrashAlt} />
                            </Button>
                          )}
                        </span>
                      </OverlayTrigger>
                    </>
                  )}
                </div>
                <div className="d-inline-block text-right" style={{ flex: 2 }}>
                  {!isPOSExportSavedOnServer && (
                    <Button
                      variant={
                        activeSubscriptions.length == 0 ? 'warning' : 'primary'
                      }
                      disabled={
                        isGeneratingThisPOSMonthlyReport ||
                        activeSubscriptions.length == 0
                      }
                      onClick={() =>
                        openReportNameModalForRequest({
                          type: 'sequentiality',
                          displayKey: monthlyPOSReportKey,
                          startDate,
                          endDate,
                          selectedCommissionProfile
                        })
                      }
                    >
                      {activeSubscriptions.length == 0 && (
                        <FontAwesomeIcon icon={faExclamationCircle} />
                      )}
                      {isGeneratingThisPOSMonthlyReport && (
                        <Spinner size="sm" />
                      )}{' '}
                      {isGeneratingThisPOSMonthlyReport
                        ? 'Generating ...'
                        : 'Generate Sequence Report'}
                    </Button>
                  )}

                  {isPOSExportSavedOnServer && (
                    <>
                      <OverlayTrigger
                        placement="auto"
                        overlay={
                          <Tooltip>
                            Created on{' '}
                            {moment(isPOSExportSavedOnServer.Created)
                              .local()
                              .format('MMMM Do YYYY, h:mm:ss a')}{' '}
                            <br />
                            {isPOSExportSavedOnServer.AdditionalData}
                          </Tooltip>
                        }
                      >
                        <a
                          className={`btn ${
                            _.includes(
                              _.map(downloadReportsContent, 'guid'),
                              isPOSExportSavedOnServer.GUID
                            )
                              ? 'btn-success'
                              : 'btn-primary'
                          }`}
                          disabled={
                            isPOSExportSavedOnServer &&
                            isPOSExportSavedOnServer.GUID === downloadingExport
                          }
                          download={`journal-export-${isPOSExportSavedOnServer.GUID}.pdf`}
                          href={
                            _.includes(
                              _.map(downloadReportsContent, 'guid'),
                              isPOSExportSavedOnServer.GUID
                            )
                              ? _.find(
                                  downloadReportsContent,
                                  d => d.guid == isPOSExportSavedOnServer.GUID
                                )?.content
                              : '#'
                          }
                          onClick={e => {
                            if (
                              !_.includes(
                                _.map(downloadReportsContent, 'guid'),
                                isPOSExportSavedOnServer.GUID
                              )
                            ) {
                              e.preventDefault();
                              downloadExportedSavedPdf(
                                isPOSExportSavedOnServer.GUID
                              );
                            }
                          }}
                        >
                          {isPOSExportSavedOnServer.GUID ===
                          downloadingExport ? (
                            <>
                              <Spinner size="sm" /> Downloading...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faCloudDownloadAlt} />{' '}
                              Download Sequence Report
                            </>
                          )}
                        </a>
                      </OverlayTrigger>{' '}
                      &nbsp;
                      <OverlayTrigger
                        placement="auto"
                        overlay={
                          <Tooltip>
                            Auto delete by{' '}
                            {moment(isPOSExportSavedOnServer.Expires)
                              .local()
                              .format('MMMM Do YYYY, h:mm:ss a')}
                          </Tooltip>
                        }
                      >
                        <span>
                          {deletingSavedExport ===
                          isPOSExportSavedOnServer.GUID ? (
                            <Spinner size="sm" />
                          ) : (
                            <Button
                              variant="outline-danger"
                              onClick={() =>
                                removeSavedExportReport(
                                  isPOSExportSavedOnServer.GUID
                                )
                              }
                            >
                              <FontAwesomeIcon icon={faTrashAlt} />
                            </Button>
                          )}
                        </span>
                      </OverlayTrigger>
                    </>
                  )}
                </div>
              </ListGroupItem>
            );
          })}
        </ListGroup>
      </Col>
    );
  };

  const IconAlertCode = () => {
    return (
      <Alert variant="info">
        <Alert.Heading className="fs-1">
          <FontAwesomeIcon icon="info-circle" className="fs-1" /> Important
          information:
        </Alert.Heading>
        <p className="mb-0">
          Due to the sensitive nature of the data stored in these entries, this
          is normally recommended for very specific situations such as
          undergoing a journal audit or submitting your journal to a governing
          body upon request. Jurat inc is not liable for the loss or misuse of
          data once it has been exported.
        </p>
        <hr className="my-2" />
        <p className="mb-0">
          Note - Depending on the amount of entries you completed in the
          specified time frame, the report may take a few minutes to finalize.
          Please be patient!
        </p>
      </Alert>
    );
  };

  useEffect(() => {
    getReportsDisplayDateRange();
  }, []);

  const openReportNameModalForRequest = request => {
    setPendingReportRequest(request);
    setReportNameInput('');
    setShowReportNameModal(true);
  };

  const closeReportNameModal = () => {
    setShowReportNameModal(false);
    setPendingReportRequest(null);
    setReportNameInput('');
  };

  const submitReportNameAndGenerate = () => {
    if (!pendingReportRequest) {
      closeReportNameModal();
      return;
    }

    const reportName = (reportNameInput || '').trim();

    if (pendingReportRequest.type === 'daterange') {
      generateReportForDateRange(
        pendingReportRequest.displayKey,
        pendingReportRequest.startDate,
        pendingReportRequest.endDate,
        pendingReportRequest.selectedCommissionProfile,
        reportName
      );
    } else if (pendingReportRequest.type === 'sequentiality') {
      generatePOSReportForDateRange(
        pendingReportRequest.displayKey,
        pendingReportRequest.startDate,
        pendingReportRequest.endDate,
        pendingReportRequest.selectedCommissionProfile,
        reportName
      );
    } else if (pendingReportRequest.type === 'custom-daterange') {
      generateReportForCustomDateRange(reportName);
    }

    closeReportNameModal();
  };

  return (
    <>
      <Modal show={showReportNameModal} onHide={closeReportNameModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Report name (optional)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="reportNameOptional">
            <Form.Label>Report name</Form.Label>
            <Form.Control
              type="text"
              value={reportNameInput}
              placeholder="(Optional) e.g. April Audit"
              onChange={e => setReportNameInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitReportNameAndGenerate();
                }
              }}
              autoFocus
            />
            <Form.Text className="text-muted">
              Leave blank to generate without a custom name.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeReportNameModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submitReportNameAndGenerate}>
            Generate
          </Button>
        </Modal.Footer>
      </Modal>
      {/* <PageHeader
        preTitle="Reports"
        title="Bulk Journal Exports"
        description="Generate and download your notary journal entries. All exports are generated as a PDF file for easy viewing."
        className="mb-3"
        titleTag="h2"
        image={corner3}
      />  */}

      {/* <div className="pb-3">
        <Alert variant="warning">
          <Alert.Heading>
            <FontAwesomeIcon icon={faInfoCircle} /> Bulk Journal Export Feature
            Under Reconstruction
          </Alert.Heading>
          <p>
            The bulk journal export experience is currently being reimagined &
            rebuilt. You can still export single entries when viewing them from
            your journal. If you need any assistance with functionality that is
            currently unavailable, please contact us at
            <a href="mailto:support@juratinc.com" className="ms-1">
              support@juratinc.com
            </a>
          </p>
        </Alert>
      </div> */}
      <IconAlertCode
        variant="info"
        message="Any saved drafts you may have are stored on your device only, until they are completed and uploaded to our servers."
      />
      {activeSubscriptions.length === 0 && (
        <div className="mt-3">
          <Alert variant="warning">
            <Alert.Heading className="fs-1">
              <FontAwesomeIcon icon={faInfoCircle} /> Subscription Required
            </Alert.Heading>
            <p className="mb-0">
              You have no active subscription and your signings are queued for
              deletion. Reactivate your subscription in the Account tab.
            </p>
          </Alert>
        </div>
      )}

      <Row className="mb-3 g-3">
        <Col lg={12}>
          <Card className="mb-3">
            <FalconComponentCard.Header
              image={corner3}
              className={'px-4 pb-0'}
              title="Bulk Journal Exports"
              subtitle="Generate and download your notary journal entries. All exports are generated as a PDF file for easy viewing."
              onRefresh={() => refetch()}
              loading={loading}
              endEl={<></>}
            />
            <Card.Header className="px-4">
              <Flex
                justifyContent="between"
                alignItems="center"
                className="border-bottom border-2"
              >
                <Nav
                  variant="tabs"
                  defaultActiveKey="monthly"
                  className="mx-0 my-0 border-0"
                  activeKey={enbaleCustomReportExport ? 'custom' : 'monthly'}
                  onSelect={() =>
                    setEnbaleCustomReportExport(!enbaleCustomReportExport)
                  }
                >
                  <Nav.Item>
                    <Nav.Link eventKey="monthly" className="pb-3 px-0 pe-4">
                      <FontAwesomeIcon icon={faCalendarAlt} /> &nbsp;Monthly
                      Report
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="custom" className="pb-3 px-4 pe-4">
                      <FontAwesomeIcon icon={faCalendarAlt} /> &nbsp;Custom
                      Range
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <Form.Group
                  as={Col}
                  md={'auto'}
                  controlId="start"
                  className="mx-1"
                >
                  {/* <Form.Label>
                Reports
              </Form.Label> */}
                  <Dropdown align="start" className="filter-dropdown">
                    <Dropdown.Toggle
                      variant="outline-primary"
                      size="sm"
                      id="commission-profile-dropdown-reporting"
                      style={{ minWidth: '200px' }}
                    >
                      {getSelectedProfileTitle()}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="filter-dropdown-menu">
                      <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                        {getDisplayProfiles().length === 0 ? (
                          <>
                            <Dropdown.Item disabled>
                              No active Commission Profiles available.
                            </Dropdown.Item>
                            <Dropdown.Item disabled>
                              Please add new profile from Notary Profile
                              Commission.
                            </Dropdown.Item>
                          </>
                        ) : (
                          <>
                            {getDisplayProfiles().map(option => (
                              <Dropdown.Item
                                key={option.GUID}
                                className={
                                  selectedCommissionProfile === option.GUID
                                    ? 'text-primary fw-semi-bold active'
                                    : ''
                                }
                                onClick={() =>
                                  changeCommissionProfile(option.GUID)
                                }
                                eventKey={option.GUID}
                              >
                                {option.CommissionedName}{' '}
                                {option.CommissionNumber} /{' '}
                                {
                                  userHelper.getTimezoneObj(
                                    option.LocalTimezone
                                  )?.name
                                }
                              </Dropdown.Item>
                            ))}
                            <Dropdown.Divider />
                            <Dropdown.Item
                              eventKey="clear"
                              onClick={() => changeCommissionProfile('')}
                              className={
                                !selectedCommissionProfile
                                  ? 'text-primary fw-semi-bold'
                                  : ''
                              }
                            >
                              Show All
                            </Dropdown.Item>
                          </>
                        )}
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </Form.Group>
              </Flex>
              {config.generateReportOnServer &&
                generatingReportsDisplayKeys.length > 0 && (
                  <div className="d-flex align-items-center gap-2 mt-4">
                    <span className="text-muted small align-middle d-inline-flex align-items-center">
                      <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                      Report is generating (may take a few minutes). Click
                      refresh button to check when it&apos;s ready.
                    </span>
                  </div>
                )}
            </Card.Header>
            {!enbaleCustomReportExport ? (
              <>
                <Card.Body className="p-0 px-4">
                  {!loadingDisplayMonthRange || dataExportMonths?.length > 0 ? (
                    userMaster && dataExportMonths ? (
                      renderPageOfReportLinks()
                    ) : (
                      <IconAlert variant="warning">
                        <p className="mb-0">Export data is not available.</p>
                      </IconAlert>
                    )
                  ) : (
                    <Flex justifyContent="center">
                      <Spinner size="sm" /> &nbsp; Loading...
                    </Flex>
                  )}
                  {!loadingDisplayMonthRange && !dataExportMonths?.length && (
                    <div className="text-center">
                      <FontAwesomeIcon
                        className="text-muted"
                        icon={faCloudDownloadAlt}
                        size="3x"
                      />
                      <br />
                      <p className="my-3">
                        No Journal data available to export
                      </p>
                    </div>
                  )}
                </Card.Body>
              </>
            ) : (
              <>
                <Card.Body className="p-0 px-2">
                  <Col sm={12}>
                    <Row>
                      <Col sm={12} md={12}>
                        <Flex
                          justifyContent={'start'}
                          direction="row"
                          className="px-3 my-1"
                        >
                          <Form.Group
                            as={Col}
                            md={'auto'}
                            controlId="start"
                            className="mx-1"
                          >
                            <Form.Label> Start Date</Form.Label>{' '}
                            <DatePicker
                              selected={
                                reportRange.start
                                  ? new Date(reportRange.start)
                                  : null
                              }
                              onChange={date =>
                                setReportRange({ ...reportRange, start: date })
                              }
                              className="form-control"
                              placeholderText="MM/DD/YYYY"
                              dateFormat="MM/dd/yyyy"
                              startDate="01/01/1950"
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              yearDropdownItemNumber={40}
                            />
                          </Form.Group>

                          <Form.Group
                            as={Col}
                            md={'auto'}
                            controlId="end"
                            className="mx-1"
                          >
                            <Form.Label> End Date</Form.Label>{' '}
                            <DatePicker
                              selected={
                                reportRange.end
                                  ? new Date(reportRange.end)
                                  : null
                              }
                              onChange={date =>
                                setReportRange({ ...reportRange, end: date })
                              }
                              className="form-control"
                              placeholderText="MM/DD/YYYY"
                              dateFormat="MM/dd/yyyy"
                              startDate="01/01/1950"
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              yearDropdownItemNumber={40}
                            />
                          </Form.Group>
                          <Col xs={4} sm={'auto'} as={Flex} alignItems="end">
                            <Button
                              disabled={
                                !reportRange.start ||
                                !reportRange.end ||
                                (config.generateReportOnServer &&
                                  generatingReportsDisplayKeys.length > 0)
                              }
                              variant="outline-primary me-3"
                              onClick={() =>
                                openReportNameModalForRequest({
                                  type: 'custom-daterange'
                                })
                              }
                            >
                              Generate Report
                            </Button>
                          </Col>
                        </Flex>
                      </Col>
                      <Col
                        sm={12}
                        className={
                          laodingReports ? 'refreshing-skeleton' : undefined
                        }
                      >
                        <Table responsive striped hover>
                          <thead>
                            <tr>
                              <th scope="col">Name</th>
                              <th scope="col">Start</th>
                              <th scope="col">End</th>
                              <th scope="col">Created</th>
                              <th scope="col">Expiring</th>
                              <th className="text-end" scope="col">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {_.map(savedExportData, report => {
                              return (
                                <tr
                                  className="align-middle"
                                  key={'saved-' + (report.GUID || report.Name)}
                                >
                                  <td className="text-nowrap">{report.Name}</td>
                                  <td className="text-nowrap">
                                    {moment(report.Start)
                                      .utc()
                                      .local()
                                      .format('MM/DD/YYYY')}
                                  </td>
                                  <td className="text-nowrap">
                                    {moment(report.End)
                                      .utc()
                                      .local()
                                      .format('MM/DD/YYYY')}
                                  </td>
                                  <td className="text-nowrap">
                                    {moment(report.Created)
                                      .utc()
                                      .local()
                                      .format('MM/DD/YYYY')}
                                  </td>
                                  <td className="text-nowrap">
                                    {moment(report.Expires)
                                      .utc()
                                      .local()
                                      .format('MM/DD/YYYY')}
                                  </td>
                                  <td className="text-end">
                                    <Button
                                      id={'btn-report-download' + report.GUID}
                                      className={`mx-1 btn ${
                                        _.includes(
                                          _.map(downloadReportsContent, 'guid'),
                                          report?.GUID
                                        )
                                          ? 'btn-success'
                                          : 'btn-primary'
                                      }`}
                                      // disabled={
                                      //   !doneGeneratingThisCustomRangeReport
                                      // }
                                      disabled={
                                        report?.GUID === downloadingExport
                                      }
                                      download={`journal-export-${report?.GUID}.pdf`}
                                      href={
                                        _.includes(
                                          _.map(downloadReportsContent, 'guid'),
                                          report?.GUID
                                        )
                                          ? _.find(
                                              downloadReportsContent,
                                              d => d.guid == report?.GUID
                                            )?.content
                                          : '#'
                                      }
                                      onClick={e => {
                                        if (
                                          !_.includes(
                                            _.map(
                                              downloadReportsContent,
                                              'guid'
                                            ),
                                            report.GUID
                                          )
                                        ) {
                                          e.preventDefault();
                                          downloadExportedSavedPdf(report.GUID);
                                        }
                                      }}
                                    >
                                      {report?.GUID === downloadingExport ? (
                                        <Spinner size="sm" animation="border" />
                                      ) : (
                                        <FontAwesomeIcon
                                          icon={faCloudDownloadAlt}
                                        />
                                      )}
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      className="mx-1"
                                      onClick={() =>
                                        removeSavedExportReport(report.GUID)
                                      }
                                    >
                                      <FontAwesomeIcon icon={faTrashAlt} />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}

                            {_.map(localExportReport, report => {
                              let isExportSavedOnServer = _.find(
                                savedExportData,
                                e => e.Name === report.displayKey
                              );
                              let isGeneratingThisCustomRangeReport = !!_.find(
                                generatingReportsDisplayKeys,
                                displayKey => report.displayKey === displayKey
                              );
                              return (
                                !isExportSavedOnServer && (
                                  <tr
                                    className="align-middle"
                                    key={'local-' + report.displayKey}
                                  >
                                    <td className="text-nowrap">
                                      {report.displayKey}
                                    </td>
                                    <td className="text-nowrap">
                                      {moment(report.begin)
                                        .utc()
                                        .local()
                                        .format('MM/DD/YYYY')}
                                    </td>
                                    <td className="text-nowrap">
                                      {moment(report.end)
                                        .utc()
                                        .local()
                                        .format('MM/DD/YYYY')}
                                    </td>
                                    <td className="text-nowrap">Now</td>
                                    <td className="text-nowrap">N/A</td>
                                    <td className="text-end">
                                      {isGeneratingThisCustomRangeReport ? (
                                        <>
                                          <Spinner size="sm" className="me-1" />
                                          <span className="text-muted small">
                                            Generating ...
                                          </span>
                                        </>
                                      ) : (
                                        <span className="text-muted small">
                                          Pending (refresh to check)
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                )
                              );
                            })}
                          </tbody>
                        </Table>
                      </Col>
                    </Row>
                  </Col>
                </Card.Body>
              </>
            )}
            <Card.Footer>
              {isGeneratingAnyReport ? (
                <Flex justifyContent="center" alignItems="center">
                  <Spinner size="sm" className="me-2" />
                  <span className="mb-0">Generating report</span>
                </Flex>
              ) : null}
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default JuratReportingList;
